import { useState } from "react";
import { useViewState } from "../../shared/useViewState";
import { useTaskActions } from "../tasks/TaskActionsContext";
import { newTaskForKeyDate, tasksForKeyDate, unchangedDateLeftOut } from "./keyDates";
import { addKeyDate, updateKeyDate, deleteKeyDate } from "./keyDateService";
import { KeyDateCard } from "./KeyDateCard";
import { KeyDateEditor } from "./KeyDateEditor";

// Key Date cards as the Key Dates page draws them, for any page that shows them (the page, and Radar's "Key Dates
// Coming Up": Craig, 24 Oct 2026, "show the actual key dates the same way they're shown on the Key Dates list page").
// One set of open cards for the session, so a card opened on one page is open on the other. Tasks open and save through
// the app's task editor (TaskActionsContext); `taskRows` is the page's useTaskRows, so a card's tasks are the page's task
// rows and the page draws one Undo message for Add to today. `goals` name each date's goal.
// Returns card(keyDate), the editor to draw, and setEditing(null | "new" | keyDate). A task that fails to save says so
// itself (DoneCheck).
export function useKeyDateCards({ uid, tasks, goals = [], taskRows }) {
  const { saveTask, openTask, newTask } = useTaskActions();
  const [editing, setEditing] = useState(null); // null, "new", or a Key Date
  // Cards start folded (Craig, 20 Oct 2026). One the user opens stays open for this session.
  const [viewState, updateViewState] = useViewState(`nudge-keydates-ui-${uid}`);
  const opened = new Set(viewState.opened || []);
  const toggleOpened = (id) => updateViewState({ opened: opened.has(id) ? [...opened].filter(x => x !== id) : [...opened, id] });
  const tasksOf = (k) => tasksForKeyDate(tasks, k.id);
  const goalOf = (k) => goals.find(g => g.id === k.goalId) || null;
  // A task's row leaves out its Key Date, named above it, and its goal when it is the date's own.
  const rowFor = (k) => (task) => taskRows.row(task, { withGoal: task.parentGoalId !== k.goalId, leaveOut: "keyDateId" });

  const card = (k) => (
    <KeyDateCard key={k.id} keyDate={k} goal={goalOf(k)} tasks={tasksOf(k)} collapsed={!opened.has(k.id)} onToggle={() => toggleOpened(k.id)}
      onEdit={() => setEditing(k)} row={rowFor(k)} onOpenTask={openTask} onUndo={(task) => saveTask(task.id, { done: false })}
      onNewTask={() => newTask(newTaskForKeyDate(k, goals))} />
  );
  const editor = editing && (
    <KeyDateEditor keyDate={editing === "new" ? null : editing} goals={goals} taskCount={editing === "new" ? 0 : tasksOf(editing).length}
      onSave={(fields, taskTexts) => (editing === "new" ? addKeyDate(uid, fields, taskTexts) : updateKeyDate(uid, editing.id, unchangedDateLeftOut(fields, editing)))}
      onDelete={() => deleteKeyDate(uid, editing.id, tasksOf(editing).map(t => t.id))}
      onClose={() => setEditing(null)} />
  );
  return { card, editor, setEditing, tasksOf, goalOf };
}
